import {
  FormWarningIcon,
  InputStyleProps,
  commonInputStyle,
} from '@ardoq/forms';
import styled, { css } from 'styled-components';
import { ControlProps, SingleValue } from 'react-select';
import { CommonSelectProps, SelectOption } from '../../types';
import { FormErrorIcon } from '@ardoq/forms';
import { INPUT_SELECTOR } from './Input';
import { PLACEHOLDER_SELECTOR } from './Placeholder';
import { colors, s8, space } from '@ardoq/design-tokens';
import { ICON_SELECTOR, Icon, IconSize } from '@ardoq/icons';
import { CustomOption } from '@ardoq/custom-select-options';

const ControlWrapper = styled.div<
  InputStyleProps & {
    $isMulti?: boolean;
    $isReadOnly?: boolean;
    $hasValue?: boolean;
    $hasLeftContent?: boolean;
    $hasContent?: boolean;
    $hasRightContent?: boolean;
  }
>`
  align-items: center;
  cursor: default;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  position: relative;
  ${PLACEHOLDER_SELECTOR} {
    color: ${colors.textSubtle};
  }
  background-color: ${colors.surfaceDefault};
  ${commonInputStyle};
  padding-right: ${s8};
  min-height: 34px;
  ${({ $hasLeftContent }) =>
    $hasLeftContent &&
    css`
      padding-left: ${s8};
    `}

  /**
   * Ideally the right content would fit in the current line height, but currently some
   * of the content like tags won't fit. Therefor we shave off 2px from the top and bottom
   * padding and increase the line height with the same amount to 24px, which lets us maintain
   * the same input height.
   */
  ${({ $hasRightContent }) =>
    $hasRightContent &&
    css`
      padding-top: ${space.xsmall};
      padding-bottom: ${space.xsmall};
      line-height: 24px;
    `}

  ${FormErrorIcon}, ${FormWarningIcon} {
    margin-right: ${s8};
  }

  ${ICON_SELECTOR}.option-icon {
    color: ${colors.icon};
  }

  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      pointer-events: none;
      color: ${colors.textDisabled};
      background-color: ${colors.surfaceDisabled};
      ${ICON_SELECTOR}, ${ICON_SELECTOR}.option-icon {
        color: ${colors.iconActionDisabled};
      }
      ${PLACEHOLDER_SELECTOR} {
        color: ${colors.textDisabled};
      }
    `}

  ${({ $isReadOnly }) =>
    $isReadOnly &&
    css`
      pointer-events: none;
      background-color: ${colors.surfaceStatusNeutral};
      ${ICON_SELECTOR}, ${ICON_SELECTOR}.option-icon {
        color: ${colors.iconActionDisabled};
      }
    `}

  ${({ $isMulti, $hasValue }) =>
    $isMulti &&
    css`
      /*
        The 0px and 4px are custom spacings enforced by
        ReactSelect containers structure
      */
      padding: 4px ${s8} 4px;
      ${INPUT_SELECTOR} {
        margin-left: ${$hasValue ? 0 : s8};
      }
      ${PLACEHOLDER_SELECTOR} {
        margin-left: ${s8};
      }
    `}

  ${({ $hasContent }) =>
    $hasContent &&
    css`
      gap: ${s8};
      padding: ${s8};
    `}
`;

export const Control = (
  props: ControlProps<SelectOption<unknown>> & {
    style: React.CSSProperties;
  }
) => {
  const {
    children,
    innerRef,
    innerProps,
    isFocused,
    isDisabled,
    isMulti,
    style,
    selectProps,
  } = props;
  const {
    errorMessage,
    hasError,
    warningMessage,
    hasWarning,
    isReadOnly,
    iconName: globalIconName,
    hideSelectedOptionLabelLeftContent,
  } = selectProps as CommonSelectProps;
  const value = !isMulti
    ? (props.selectProps.value as SingleValue<SelectOption<unknown>>)
    : undefined;

  const hasValue = Boolean(
    ((props.selectProps.value || []) as SelectOption<unknown>[]).length
  );
  const {
    iconName: optionIconName,
    leftContent,
    leftIcon,
    customData,
  } = value || {};
  const leftIconName = leftIcon?.name;
  const iconName = leftIconName || optionIconName || globalIconName;
  const hasRightContent = Boolean(value?.rightContent);
  const hasLeftContent =
    Boolean(iconName || value?.leftContent) &&
    !hideSelectedOptionLabelLeftContent;
  return (
    <ControlWrapper
      ref={innerRef as any}
      {...innerProps}
      $isFocused={isFocused}
      $isDisabled={isDisabled}
      $isReadOnly={isReadOnly}
      $hasLeftContent={hasLeftContent}
      $hasRightContent={hasRightContent}
      $hasError={hasError || Boolean(errorMessage)}
      $hasWarning={
        !(hasError || Boolean(errorMessage)) &&
        (hasWarning || Boolean(warningMessage))
      }
      $isMulti={isMulti}
      $hasValue={hasValue}
      $hasContent={Boolean(customData)}
      style={style}
    >
      {!leftContent && iconName && !hideSelectedOptionLabelLeftContent && (
        <Icon
          iconName={iconName}
          className="option-icon"
          iconSize={IconSize.MEDIUM}
        />
      )}
      {leftContent}
      <CustomOption customData={customData} />
      {children}
    </ControlWrapper>
  );
};
